import React from 'react';
import { Grid, Typography, Paper, Chip, Box, Divider } from '@mui/material';
import Tile from 'components/Tile/Tile';
import { useWorkflowStyles } from './styles';
import { WorkflowActionType } from './constants';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';

interface WorkflowNodeActionProps {
  selectedActionConfiguration: any;
  onClick: () => void;
}

const WorkflowNodeAction: React.FC<WorkflowNodeActionProps> = ({ selectedActionConfiguration, onClick }) => {
  const classes = useWorkflowStyles();

  const renderCreateTaskAction = (config: any) => (
    <Paper
      elevation={0}
      style={{
        padding: 20,
        backgroundColor: '#F8F9FA',
        marginTop: 12,
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <AssignmentIcon style={{ color: '#1976d2', marginRight: 8 }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1976d2' }}>
          Create Task
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="start" gap={1}>
            <InfoIcon style={{ fontSize: 18, color: '#666', marginTop: 2 }} />
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Regarding
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {config.regarding}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="start" gap={1}>
            <CalendarTodayIcon style={{ fontSize: 18, color: '#666', marginTop: 2 }} />
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Due Date
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {new Date(config.dueDateTime).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="start" gap={1}>
            <Box
              style={{
                width: 18,
                height: 18,
                marginTop: 2,
              }}
            >
              <Chip
                label={config.status}
                size="small"
                style={{
                  height: 20,
                  fontSize: 11,
                  backgroundColor: config.status === 'Open' ? '#4caf50' : '#ff9800',
                  color: 'white',
                }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Status
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="start" gap={1}>
            <PersonIcon style={{ fontSize: 18, color: '#666', marginTop: 2 }} />
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Assigned To
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {config.assignedToUser}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {config.taskItems && config.taskItems.length > 0 && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
            Task Items ({config.taskItems.length})
          </Typography>

          <Grid container spacing={2}>
            {config.taskItems.map((item: any, index: number) => (
              <Grid item key={index} xs={12}>
                <Paper
                  elevation={0}
                  style={{
                    padding: 12,
                    backgroundColor: 'white',
                    border: '1px solid #E0E0E0',
                    borderRadius: '6px',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <Chip
                      label={`#${index + 1}`}
                      size="small"
                      style={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        marginRight: 8,
                        backgroundColor: '#E3F2FD',
                        color: '#1976d2',
                      }}
                    />
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {item.type}
                    </Typography>
                  </Box>

                  {item.docType && item.type !== 'info' && (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                      Document: <span style={{ color: '#333', fontWeight: 500 }}>{item.docType}</span>
                    </Typography>
                  )}

                  {item.taskAssignorNotes && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Notes: <span style={{ color: '#333', fontWeight: 500 }}>{item.taskAssignorNotes}</span>
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Paper>
  );

  const renderSendEmailAction = (config: any) => (
    <Paper
      elevation={0}
      style={{
        padding: 20,
        backgroundColor: '#F8F9FA',
        marginTop: 12,
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <EmailIcon style={{ color: '#1976d2', marginRight: 8 }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1976d2' }}>
          Send Email
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="start" gap={1}>
            <AssignmentIcon style={{ fontSize: 18, color: '#666', marginTop: 2 }} />
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary" display="block">
                Email Template
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {config.templateName || 'Not specified'}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon style={{ fontSize: 18, color: '#666' }} />
            <Box flex={1}>
              <Typography variant="caption" color="textSecondary" display="block">
                CC Triggered User
              </Typography>
              <Chip
                label={config.ccTriggeredUser ? 'Yes' : 'No'}
                size="small"
                style={{
                  marginTop: 4,
                  height: 24,
                  backgroundColor: config.ccTriggeredUser ? '#4caf50' : '#9e9e9e',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>
        </Grid>

        {config.additionalRecipients && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="start" gap={1}>
              <EmailIcon style={{ fontSize: 18, color: '#666', marginTop: 2 }} />
              <Box flex={1}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Additional Recipients
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 500, marginTop: 4 }}>
                  {config.additionalRecipients}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  return (
    <Grid item xs={8} className={classes.nodeContainer} onClick={onClick}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tile className={classes.nodeTitle}>Action</Tile>
        </Grid>

        <Grid item xs={12}>
          {selectedActionConfiguration ? (
            <>
              {selectedActionConfiguration.CREATE_TASK &&
                renderCreateTaskAction(selectedActionConfiguration.CREATE_TASK)}
              {selectedActionConfiguration.SEND_EMAIL && renderSendEmailAction(selectedActionConfiguration.SEND_EMAIL)}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Click here to set Action
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default WorkflowNodeAction;
